/**
 * Reports routes (IA §2 Manage › Reports) and the two public capability-link pages
 * (IA §2 "Public": /share/:token, /tv/:token), which hang under the public route — outside the
 * app shell and without a session.
 */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { publicRoute } from '@/app/publicRoute';
import { str } from '@/lib/search';

export interface ReportsSearch {
  q?: string;
  archived?: 'yes';
  sort?: string;
  /** Open the "new report" dialog (optionally with a template). */
  new?: string;
}

export interface ReportSearch {
  tab?: string;
}

export interface SharesSearch {
  purpose?: 'report_view' | 'tv_leaderboard';
  revoked?: 'yes';
  sort?: string;
}

export interface ScreensSearch {
  /** Open the "new TV screen" dialog for drivers or vehicles (from the rankings). */
  create?: 'driver' | 'vehicle';
  sort?: 'safety' | 'eco';
  revoked?: 'yes';
}

export const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  staticData: { permission: 'reports.report.read' },
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    q: str(s.q),
    archived: s.archived === 'yes' ? 'yes' : undefined,
    sort: str(s.sort),
    new: str(s.new),
  }),
  component: lazyRouteComponent(() => import('./pages/ReportsPage')),
});

export const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/$reportId',
  staticData: { permission: 'reports.report.read' },
  validateSearch: (s: Record<string, unknown>): ReportSearch => ({ tab: str(s.tab) }),
  component: lazyRouteComponent(() => import('./pages/ReportPage')),
});

export const runRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/$reportId/runs/$runId',
  staticData: { permission: 'reports.report.read' },
  component: lazyRouteComponent(() => import('./pages/RunPage')),
});

export const sharesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/links',
  staticData: { permission: 'reports.share.manage' },
  validateSearch: (s: Record<string, unknown>): SharesSearch => ({
    purpose: s.purpose === 'report_view' || s.purpose === 'tv_leaderboard' ? s.purpose : undefined,
    revoked: s.revoked === 'yes' ? 'yes' : undefined,
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./pages/SharesPage')),
});

export const screensRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/screens',
  staticData: { permission: 'reports.share.manage' },
  validateSearch: (s: Record<string, unknown>): ScreensSearch => ({
    create: s.create === 'driver' || s.create === 'vehicle' ? s.create : undefined,
    sort: s.sort === 'safety' || s.sort === 'eco' ? s.sort : undefined,
    revoked: s.revoked === 'yes' ? 'yes' : undefined,
  }),
  component: lazyRouteComponent(() => import('./pages/ScreensPage')),
});

export const exportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/exports',
  staticData: { permission: 'reports.report.read' },
  component: lazyRouteComponent(() => import('./pages/ExportsPage')),
});

/*
 * Public pages (the module's `publicRoutes`): under the public route, so the app route's session
 * check and the app shell never run for them.
 */
export const shareRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/share/$token',
  component: lazyRouteComponent(() => import('./public/SharePage')),
});

export const tvRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/tv/$token',
  component: lazyRouteComponent(() => import('./public/TvPage')),
});
